import { AxiosError, AxiosResponse } from 'axios';
import instance, { ApiError } from '../../api/instance';
import { CompanyPackage, DraftCompanyPackage } from './companyPackages.types';
import { UploadFileToBucket } from '../../api/utils/uploadFileToBucket';

const companyPackagesApiPath = (companyId: string) => `/companies/${companyId}/packages`;

export const getPackages = async ({
  companyId,
}: {
  companyId: string;
}): Promise<AxiosResponse<CompanyPackage[] & ApiError>> => {
  return instance
    .get<CompanyPackage[]>(companyPackagesApiPath(companyId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const createPackage = async ({
  companyId,
  data,
}: {
  companyId: string;
  data: Omit<DraftCompanyPackage, 'vipAccess' | 'promo' | 'isKids'> & { name: string };
}): Promise<AxiosResponse<CompanyPackage & ApiError>> => {
  return instance
    .post<CompanyPackage>(companyPackagesApiPath(companyId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const companyPackageApiPath = (companyId: string, packageId: string) => `/companies/${companyId}/packages/${packageId}`;

export const getPackage = async ({
  companyId,
  packageId,
}: {
  companyId: string;
  packageId: string;
}): Promise<AxiosResponse<CompanyPackage & ApiError>> => {
  return instance
    .get<CompanyPackage>(companyPackageApiPath(companyId, packageId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const updatePackage = async ({
  companyId,
  packageId,
  data,
}: {
  companyId: string;
  packageId: string;
  data: Partial<CompanyPackage>;
}): Promise<AxiosResponse<CompanyPackage & ApiError>> => {
  return instance
    .post<CompanyPackage>(companyPackageApiPath(companyId, packageId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const deletePackage = async ({
  companyId,
  packageId,
}: {
  companyId: string;
  packageId: string;
}): Promise<AxiosResponse<CompanyPackage & ApiError>> => {
  return instance
    .delete<CompanyPackage>(companyPackageApiPath(companyId, packageId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export type UploadPackageImage = (params: {
  companyId: string;
  packageId: string;
  data: { filename: string };
}) => Promise<AxiosResponse<Parameters<UploadFileToBucket>[0]>>;

export const uploadPackageImage: UploadPackageImage = async ({ companyId, data }) => {
  return instance.post(`/companies/${companyId}/packages/images`, data).catch(e => e.response as never);
};

export type PostPackageActivate = (params: {
  companyId: string;
  packageId: string;
}) => Promise<AxiosResponse<CompanyPackage>>;

export const postPackageActivate: PostPackageActivate = async ({ companyId, packageId }) => {
  return instance.post(`/companies/${companyId}/packages/${packageId}/activate`).catch(e => e.response as never);
};
